import SEO from "../../../components/seo.jsx"
import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import HeroImg from "../../../assets/beard-hair-transplant.png";
import BreadcrumbImg from "../../../assets/beardHt-breadcrumb-bg.jpg";
import SurgeonImg from "../../../assets/team/dr-ajinkya.png";
import ProcedureImg from "../../../assets/beardHt-procedure-closeup.jpg";
import TechniquesImg from "../../../assets/beardHt-hero.jpg"
import ResultImg from "../../../assets/beard-transplant.png";

gsap.registerPlugin(ScrollTrigger);
const cinzel = { fontFamily: "'Cinzel', serif" };

/* ============================================================
   CONTENT pulled straight from the PHP "Beard Hair Transplant" page
============================================================ */
const PAGE = {
  hero: {
    eyebrow: "Facial Hair Restoration",
    heading: "Beard Hair Transplant",
    sub: "Natural, fuller facial hair restored with precision by Dr.Ajinkya Patil at Clinique Internationale, Pune.",
  },

  breadcrumb: [
    { label: "Home", href: "/" },
    { label: "Beard Hair Transplant", href: null },
  ],

  intro: {
    eyebrow: "Overview",
    heading: "Beard Hair Transplant in Pune",
    paragraphs: [
      "People opt for beard hair transplants to address various concerns. Some individuals face genetic factors causing limited facial hair growth or patches, while others might have scarring due to trauma or surgery in the beard area. Beard transplants also cater to those desiring a more masculine appearance or looking to conceal certain facial irregularities.",
      "A beard hair transplant at Clinique Internationale in Pune involves the extraction and transplantation of hair follicles from a donor area often the scalp to the beard region. This procedure is sought by individuals with sparse or patchy beard growth, aiming to achieve a fuller, denser, and more aesthetically pleasing beard.",
      "Clinique Internationale specialises in this procedure, utilising advanced techniques like Follicular Unit Extraction (FUE) or Follicular Unit Transplantation (FUT) to ensure natural-looking results, with a personalised approach for each individual.",
    ],
  },

  surgeon: {
    eyebrow: "Lead Surgeon",
    heading: "Beard Hair Transplant Surgeon in Pune",
    paragraphs: [
      "Dr. Ajinkya Patil, a renowned hair restoration specialist in Pune, excels in beard hair transplant procedures. With expertise in advanced techniques like FUE and FUT, Dr. Ajinkya Patil is recognised for his precision and natural-looking results.",
      "His proficiency in restoring facial hair growth caters to individuals seeking fuller, denser beards. As a prominent figure in the field of hair restoration, Dr. Ajinkya Patil offers personalised care, ensuring patients achieve their desired beard aesthetic with expertise and innovation.",
    ],
  },

  cost: {
    eyebrow: "Pricing",
    heading: "Beard Hair Transplant Cost",
    intro:
      "The cost of a beard hair transplant in Pune generally ranges from ₹40,000 to ₹1,50,000. Prices vary based on the clinic, technique used, extent of treatment, and individual requirements.",
    cities: [
      { city: "Pune", range: "₹35,000 – ₹1,50,000", href: "/" },
      { city: "Bangalore", range: "₹40,000 – ₹1,80,000", href: "https://bangalore.hairclinique.in/" },
      { city: "Mumbai", range: "₹45,000 – ₹2,00,000", href: "https://hairclinique.in/" },
    ],
  },

  techniques: {
    eyebrow: "The Procedures",
    heading: "Beard Hair Transplant Techniques",
    intro: "The two primary types of beard transplant procedures are Follicular Unit Extraction (FUE) and Follicular Unit Transplantation (FUT).",
    items: [
      {
        num: "01",
        title: "Follicular Unit Extraction (FUE)",
        desc: "Involves individually extracting hair follicles from a donor site usually the back of the scalp and implanting them into the beard area. It's minimally invasive, leaves tiny scars, and allows for a quicker recovery.",
      },
      {
        num: "02",
        title: "Follicular Unit Transplantation (FUT)",
        desc: "Involves harvesting a strip of skin from the donor area, then dissecting it into individual follicular units for transplantation into the beard region. May leave a linear scar but allows a larger number of grafts in one session.",
      },
    ],
    outro:
      "The ultimate goal of both procedures is a realistic display of beard growth, increasing density and mitigating patchiness. The decision between FUE and FUT depends on personal preference, the amount of transplantation required, and the surgeon's advice.",
  },

  benefits: {
    eyebrow: "Why It's Worth It",
    heading: "Benefits of Beard Hair Transplant",
    items: [
      { title: "Enhanced Facial Aesthetics", desc: "Beard transplants help redefine facial features, framing the face and creating a more balanced, masculine appearance." },
      { title: "Improved Confidence", desc: "For those with sparse or patchy beard growth, a fuller and denser beard often boosts self-esteem and confidence." },
      { title: "Permanent Solution", desc: "Transplanted hair follicles continue to grow naturally, providing a long-term solution for a permanent improvement in beard appearance." },
      { title: "Customised Results", desc: "Surgeons can precisely design and customise the beard according to the patient's preferences, ensuring a natural, tailored outcome." },
      { title: "Versatility in Styling", desc: "A fuller beard offers more versatility in grooming and styling, allowing individuals to explore various beard styles and trends." },
    ],
  },

  cta: {
    eyebrow: "Take the First Step",
    heading: "Ready for a Fuller, More Defined Beard?",
    sub: "Book a personalised consultation with Dr. Ajinkya Patil at Clinique Internationale and take the first step toward natural, lasting results.",
    primary: { label: "Book Consultation", href: "https://api.whatsapp.com/send?phone=919082123009&text=I%27m%20interested%20in%20Hair%20Restoration" },
    secondary: { label: "View All Treatments", href: "/about-hair-transplants.php" },
  },
};

/* ============================================================
   REUSABLE SUB-COMPONENTS
============================================================ */
function Eyebrow({ children, light = false, align = "left" }) {
  return (
    <p
      style={cinzel}
      className={`text-[10.5px] tracking-[0.28em] uppercase mb-3 flex items-center gap-2.5 ${
        align === "center" ? "justify-center" : "justify-start"
      } ${light ? "text-[#c9a84c]/70" : "text-[#a07828]"}`}
    >
      <span className={`w-6 h-px ${light ? "bg-[#c9a84c]/50" : "bg-[#c9a84c]"}`} />
      {children}
    </p>
  );
}

function SectionHeading({ children, light = false, align = "left" }) {
  return (
    <h2
      style={cinzel}
      className={`font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.85rem] uppercase tracking-[0.05em] mb-8 ${
        align === "center" ? "text-center" : "text-left"
      } ${light ? "text-white" : "text-[#1a0f02]"}`}
    >
      {children}
    </h2>
  );
}

function GoldDivider() {
  return <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />;
}

function FramedImage({ src, alt, className = "" }) {
  return (
    <div className={`relative rounded-2xl overflow-hidden border border-[#c9a84c]/25 ${className}`}>
      <img src={src} alt={alt} className="w-full h-full object-contain" />
      <span className="absolute inset-0 ring-1 ring-inset ring-[#c9a84c]/15 rounded-2xl pointer-events-none" />
    </div>
  );
}

/* ============================================================
   PAGE COMPONENT
============================================================ */
export default function BeardHairTransplantPune({ url = "https://pune.hairclinique.in/beard-hair-transplants.php" }) {
  const pageRef = useRef(null);
  const heroEyeRef = useRef(null);
  const heroHeadRef = useRef(null);
  const heroSubRef = useRef(null);
  const sectionRefs = useRef([]);
  const addSection = (el) => el && !sectionRefs.current.includes(el) && sectionRefs.current.push(el);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";
      gsap.fromTo(heroEyeRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ease });
      gsap.fromTo(heroHeadRef.current, { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.75, ease, delay: 0.1 });
      gsap.fromTo(heroSubRef.current, { opacity: 0, y: 22 }, { opacity: 1, y: 0, duration: 0.7, ease, delay: 0.18 });

      sectionRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(
          el,
          { opacity: 0, y: 36 },
          {
            opacity: 1,
            y: 0,
            duration: 0.75,
            ease,
            scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none reverse" },
          }
        );
      });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  /* full-bleed wrapper: fills the viewport with generous edge padding instead of a centered max-width column */
  const wrap = "w-full px-5 sm:px-10 lg:px-16 xl:px-24";
  const sec = "py-16 sm:py-20 md:py-24";

  return (
    <>
    <SEO
  title="Beard Hair Transplant in Pune | Clinique Internationale"
  description="Get natural-looking beard hair transplant results in Pune at Clinique Internationale, led by Dr. Ajinkya Patil. Book your consultation today."
  url={url}
/>
      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap"
        rel="stylesheet"
      />

      <div
        ref={pageRef}
        className="relative w-full bg-[#f2ece0] overflow-hidden mt-20"
        style={{ fontFamily: "'Raleway', sans-serif" }}
      >
        {/* ══════════════════════════════════════
            HERO full width
        ══════════════════════════════════════ */}
<div className="relative w-full h-[280px] sm:h-[340px] md:h-[700px] flex items-center justify-end overflow-hidden">
          <img className="absolute inset-0 w-full h-full object-cover object-center" src={HeroImg} alt={PAGE.hero.heading} />
          <div className="absolute inset-0" />
          <div
            className="absolute inset-0 opacity-40 pointer-events-none"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")",
            }}
          />
          <div className="relative z-10 text-right px-6 sm:px-10 md:px-16 max-w-xl">
            <p ref={heroEyeRef} style={cinzel} className="text-[10.5px] tracking-[0.32em] uppercase text-black mb-4 flex items-center justify-end gap-3">
              <span className="w-7 h-px bg-black" />
              {PAGE.hero.eyebrow}
              <span className="w-7 h-px bg-black" />
            </p>
            <h1
              ref={heroHeadRef}
              style={cinzel}
              className="font-black text-[2rem] sm:text-[2.8rem] md:text-[3.4rem] tracking-[0.04em] uppercase text-black mb-3 leading-tight"
            >
              {PAGE.hero.heading}
            </h1>
            <p ref={heroSubRef} className="text-[14px] sm:text-[15px] text-black/70 leading-relaxed ml-auto">
              {PAGE.hero.sub}
            </p>
          </div>
        </div>

        {/* ── BREADCRUMB full width with background image ── */}
        <div className="relative w-full overflow-hidden">
          <img className="absolute inset-0 w-full h-full object-cover" src={BreadcrumbImg} alt="" aria-hidden="true" />
          <div className="absolute inset-0 bg-[#0e0905]/88" />
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/25 to-transparent" />
          <div className={`relative ${wrap} py-3.5 flex flex-wrap items-center gap-2`}>
            {PAGE.breadcrumb.map((b, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <span className="text-[#c9a84c]/30 text-xs">›</span>}
                {b.href ? (
                  <a href={b.href} style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]/60 hover:text-[#c9a84c] transition-colors">
                    {b.label}
                  </a>
                ) : (
                  <span style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]">
                    {b.label}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════
            INTRO
        ══════════════════════════════════════ */}
<div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <FramedImage src={ProcedureImg} alt="Hair transplant procedure close-up" className="h-[260px] sm:h-[320px] md:h-[380px] order-last md:order-first" />
            <div>
              <Eyebrow>{PAGE.intro.eyebrow}</Eyebrow>
              <SectionHeading>{PAGE.intro.heading}</SectionHeading>
              {PAGE.intro.paragraphs.map((p, i) => (
                <p key={i} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] mb-4">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            SURGEON (dark band, text + image)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <Eyebrow light>{PAGE.surgeon.eyebrow}</Eyebrow>
                <SectionHeading light>{PAGE.surgeon.heading}</SectionHeading>
                {PAGE.surgeon.paragraphs.map((p, i) => (
                  <p key={i} className="text-[13.5px] leading-[1.9] text-white/60 mb-4">
                    {p}
                  </p>
                ))}
              </div>
              <FramedImage src={SurgeonImg} alt="Dr. Ajinkya Patil, beard hair transplant surgeon" className="h-[260px] sm:h-[320px] md:h-[380px]" />
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            COST BY CITY
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection}>
            <Eyebrow align="center">{PAGE.cost.eyebrow}</Eyebrow>
            <SectionHeading align="center">{PAGE.cost.heading}</SectionHeading>
            <p className="text-[14.5px] leading-[1.9] text-[#3a2e1e] max-w-3xl mx-auto text-center mb-10">{PAGE.cost.intro}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
              {PAGE.cost.cities.map((c, i) => (
                <a
                  key={i}
                  href={c.href}
                  className="bg-white border border-[#c9a84c]/18 rounded-2xl p-6 relative overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_12px_32px_rgba(90,60,10,0.1)] transition-all duration-300 block text-center group"
                >
                  <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                  <div style={cinzel} className="text-[13px] font-bold tracking-[0.06em] uppercase text-[#1a0f02] mb-3 group-hover:text-[#a07828] transition-colors">
                    {c.city}
                  </div>
                  <div
                    style={cinzel}
                    className="font-black text-[1.05rem] leading-snug bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a] bg-clip-text text-transparent"
                  >
                    {c.range}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            TECHNIQUES (image + list)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#faf8f4] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow align="center">{PAGE.techniques.eyebrow}</Eyebrow>
              <SectionHeading align="center">{PAGE.techniques.heading}</SectionHeading>
              <p className="text-[14.5px] leading-[1.9] text-[#3a2e1e] max-w-3xl mx-auto text-center mb-10">{PAGE.techniques.intro}</p>

              <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
                <FramedImage src={TechniquesImg} alt="Beard hair transplant procedure close-up" className="h-[260px] sm:h-[340px] lg:h-full lg:sticky lg:top-24" />
                <div className="flex flex-col gap-5">
                  {PAGE.techniques.items.map((t) => (
                    <div key={t.num} className="bg-white border border-[#c9a84c]/18 rounded-2xl p-6 relative overflow-hidden hover:shadow-[0_8px_28px_rgba(90,60,10,0.08)] transition-shadow">
                      <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                      <div className="flex gap-5 items-start">
                        <span style={cinzel} className="font-black text-[2rem] leading-none text-[#c9a84c]/20 select-none flex-shrink-0">
                          {t.num}
                        </span>
                        <div>
                          <div style={cinzel} className="text-[13px] font-bold tracking-[0.05em] uppercase text-[#1a0f02] mb-2">
                            {t.title}
                          </div>
                          <p className="text-[13px] leading-[1.8] text-[#6b5535]">{t.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <p className="text-[13px] leading-[1.85] text-[#6b5535] italic px-1">{PAGE.techniques.outro}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            BENEFITS (dark band + image)
        ══════════════════════════════════════ */}
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow light align="center">{PAGE.benefits.eyebrow}</Eyebrow>
              <SectionHeading light align="center">{PAGE.benefits.heading}</SectionHeading>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-10 items-center">
                <FramedImage src={ResultImg} alt="Well-groomed, fuller beard result" className="h-[260px] sm:h-[320px]" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {PAGE.benefits.items.map((b, i) => (
                    <div
                      key={i}
                      className="relative overflow-hidden bg-white/[0.04] border border-[#c9a84c]/15 rounded-2xl p-5 hover:bg-[#c9a84c]/[0.07] hover:border-[#c9a84c]/30 transition-colors"
                    >
                      <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/35 to-transparent" />
                      <div className="flex items-center gap-2.5 mb-2">
                        <span style={cinzel} className="text-[11px] font-black text-[#c9a84c]">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <div style={cinzel} className="text-[11.5px] font-bold tracking-[0.05em] uppercase text-white/90">
                          {b.title}
                        </div>
                      </div>
                      <p className="text-[12.5px] leading-[1.75] text-white/55">{b.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            CTA
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="max-w-xl mx-auto text-center">
            <Eyebrow align="center">{PAGE.cta.eyebrow}</Eyebrow>
            <h3
              style={cinzel}
              className="font-black text-[1.3rem] sm:text-[1.65rem] md:text-[1.9rem] text-[#1a0f02] uppercase tracking-[0.06em] mb-3.5"
            >
              {PAGE.cta.heading}
            </h3>
            <p className="text-[14px] text-[#6b5535] leading-[1.75] mb-8">{PAGE.cta.sub}</p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <a
                href={PAGE.cta.primary.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...cinzel, backgroundImage: "linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)", backgroundSize: "200% 100%" }}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-3.5 rounded-md text-[#1a0f02] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(201,168,76,0.35)] transition-all inline-block"
              >
                {PAGE.cta.primary.label}
              </a>
              <a
                href={PAGE.cta.secondary.href}
                style={cinzel}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-[13px] rounded-md bg-transparent border border-[#1a0f02]/35 text-[#1a0f02] hover:-translate-y-0.5 hover:border-[#c9a84c] hover:text-[#a07828] transition-all inline-block"
              >
                {PAGE.cta.secondary.label}
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
